/* eslint-disable react/prop-types */
import CurrencyInput from "react-currency-input-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import AirtimeDrawer from "../drawers/airtime-drawer";
import { useNavigate } from "react-router-dom";
import AirtimeBeneficiaries from "@/routes/dashboard/buy-airtime/AirtimeBeneficiaries";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Fingerprint, ScanFace } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { toast } from "sonner";

const BuyAirtimeForm = ({
  register,
  handleSubmit,
  telcos,
  errors,
  loading,
  setLoading,
  onSubmit,
  setValue,
  watch,
  setError,
  clearErrors,
  setInsertedPhoneNumber,
  beneficiaryData,
  openAirtimeDrawer,
  setOpenAirtimeDrawer,
}) => {
  const user = useSelector((state) => state.user.user);
  const [pinError, setPinError] = useState("");
  const platform = Capacitor.getPlatform();

  const phone = watch("phone");
  const network = watch("carrier");
  const amount = watch("amount");
  const pin = watch("pin");

  // console.log(network);
  // console.log(telcos[network ]);

  const isFormValid =
    phone && network && amount && !errors.amount && pin && !pinError;

  async function authenticateWithBiometrics() {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      toast.warning(
        "Biometric authentication is not available on this device. Kindly enable biometrics in your settings.",
      );
    }

    const verified = await NativeBiometric.verifyIdentity({
      reason: "Verify Transaction",
      title: "Airtime Purchase",
      subtitle: "Please authenticate to proceed with the airtime purchase.",
    })
      .then(() => true)
      .catch(() => false);

    if (verified) {
      setValue("pin", user.pin.toString());
      setOpenAirtimeDrawer(true);
    } else {
      toast.error("Authentication failed. Please try again.");
    }
  }

  useEffect(() => {
    if (pin) {
      const userPin = user.pin.toString();
      if (pin === userPin) {
        setPinError("");
      } else {
        setPinError("Invalid transaction pin");
      }
    } else {
      setPinError("");
    }
  }, [pin, user.pin]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="grid grid-cols-1 gap-[20px]">
        <div className="col-span-1">
          <label htmlFor="phone" className="text-sm font-semibold">
            Phone Number
          </label>
          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
            <input
              autoComplete="tel"
              type="tel"
              name="phone"
              {...register("phone", {
                required: "Phone Number is required",
                pattern: {
                  value: /^[0-9]{11,12}$/,
                  message:
                    "Phone number must be 11 or 12 digits e.g 09123456789",
                },
                onChange: (e) => {
                  let value = e.target.value;
                  if (value.startsWith("+234")) {
                    value = "0" + value.slice(4);
                    e.target.value = value;
                  }
                  const cleanNumber = value.replace(/[^0-9]/g, "");
                  setInsertedPhoneNumber(cleanNumber);
                },
              })}
              className="w-full border-none bg-transparent text-sm outline-none"
              placeholder="0812-222-3343"
            />{" "}
            <AirtimeBeneficiaries
              watch={watch}
              setValue={setValue}
              telcos={telcos}
            />
          </div>

          {errors.phone && (
            <span className="text-xs font-medium text-red-500">
              {errors.phone.message}
            </span>
          )}

          {!beneficiaryData && (
            <div className="mt-1 flex items-center justify-end gap-2">
              <input
                type="checkbox"
                id="saveBenef"
                checked={watch("beneficiary")}
                onChange={() => {
                  setValue("beneficiary", !watch("beneficiary"));
                }}
              />

              <label htmlFor="saveBenef" className="font-geist text-sm">
                Save number as beneficiary
              </label>
            </div>
          )}
        </div>

        <div className="col-span-1 -mt-3">
          <label htmlFor="carrier" className="text-sm font-semibold">
            Network
          </label>

          <Select
            value={Number(watch("carrier"))}
            onValueChange={(value) => {
              setValue("carrier", value);
              // console.log(value);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Network" />
            </SelectTrigger>
            <SelectContent>
              {telcos?.map(
                (telco, index) =>
                  telco && (
                    <SelectItem key={index} value={index}>
                      {telco}
                    </SelectItem>
                  ),
              )}
            </SelectContent>
          </Select>
          {!watch("carrier") ? (
            <div className="pt-2 text-sm opacity-80">
              Click to select a network
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="col-span-1">
          <label htmlFor="amount" className="text-sm font-semibold">
            Amount
          </label>
          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
            <CurrencyInput
              prefix="₦ "
              {...register("amount", {
                required: "Amount is required",
              })}
              value={watch("amount")}
              onValueChange={(value) => {
                setValue("amount", value);

                if (value < 50) {
                  setError("amount", {
                    type: "manual",
                    message: "Amount must be greater than ₦50",
                  });
                } else {
                  clearErrors("amount");
                }
              }}
              className="w-full border-none bg-transparent text-sm outline-none"
              placeholder="Amount to transfer"
            />
          </div>
          {errors.amount && (
            <span className="text-xs font-medium text-red-500">
              {errors.amount.message}
            </span>
          )}
        </div>

        {amount && network && phone ? (
          <div className="col-span-1">
            <label htmlFor="phone" className="text-sm font-semibold">
              Transaction Pin
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                className="inputClass"
                placeholder="****"
                {...register("pin", {
                  required: "Transaction Pin is required",
                  onChange: (e) => {
                    const inputPin = e.target.value;
                    setValue("pin", inputPin);
                  },
                })}
              />
              {platform != "web" && (
                <Button
                  onClick={authenticateWithBiometrics}
                  type="button"
                  className={"h-[40px] text-white"}
                >
                  {platform == "android" ? (
                    <Fingerprint size={30} />
                  ) : (
                    <ScanFace size={30} />
                  )}{" "}
                  Use Biometrics
                </Button>
              )}
            </div>

            {pinError && (
              <p className="text-xs font-medium text-red-500">{pinError}</p>
            )}
          </div>
        ) : (
          <></>
        )}

        <AirtimeDrawer
          isFormValid={isFormValid}
          phone={phone}
          network={network}
          amount={amount}
          telcos={telcos}
          setValue={setValue}
          loading={loading}
          setLoading={setLoading}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          openAirtimeDrawer={openAirtimeDrawer}
          setOpenAirtimeDrawer={setOpenAirtimeDrawer}
        />
      </section>
    </form>
  );
};

export default BuyAirtimeForm;
